import React from 'react';
import { resolve } from 'library/helpers/path-resolver';
import { capitalize } from 'library/helpers/text';
import OutcomeCard from 'apps/journey/pages/my-journey/components/outcome';
import { boolean, object, func, string } from 'prop-types';
import useIncludedGoals from 'apps/journey/components/modals/outcomes/hooks/use-included-goals';
import * as StyledOutcomesModal from 'apps/journey/components/modals/outcomes/styled-outcomes-modal';
import ButtonDisabler from 'library/components/button-disabler';
import {
  addOutcomeMutation,
  addOutcomeMutationParseQuery,
} from 'apps/journey/queries/addOutcomeMutation';
import { rejectRecommendationAction } from 'apps/journey/queries/archiveRecommendationFromUser';
import { NotificationRender } from 'library/components/notification';
import { withRouter } from 'library/util/core-router';
import { handleCancel } from 'apps/journey/components/modals/outcomes/util/handle-cancel';
import hasHoc from 'util/hoc/has-hoc';
import outcomeQuery from 'apps/journey/queries/outcome-query';
import OutcomeGoalContext from 'apps/journey/context/outcome-goal-context';

const OutcomeGoalsCard = ({
  newOutcomes = false,
  actionButtonText,
  headerDescription,
  user,
  graph_loading,
  handle_graph_loading,
  graph_data,
  configuration,
  reloadParentGraph,
  handleAddOutcome,
  recommend_to_user,
  reloadOutcomes = () => null,
  outcomes,
  match,
  isRecommendation,
  recommendedOutcomes,
  ...props
}) => {
  if (graph_loading) return handle_graph_loading();

  const outcome = resolve(graph_data, 'outcome', {});
  /**
   * `outcomeToEdit` is the outcome that a user has assigned to
   * them already.  We want to take those goals and auto-include them
   *
   * If we _don't_ have `outcomeToEdit` defined, you can assume that
   * we are adding a new outcome, in which case we want to include all
   * goals by default.
   */
  const outcomeToEdit = (isRecommendation
    ? recommendedOutcomes
    : outcomes
  ).find((x) => x.id === outcome.id);

  let activeGoals = [];
  if (newOutcomes && !isRecommendation) {
    activeGoals = outcome?.goals;
  } else {
    activeGoals = outcomeToEdit?.goals;
  }

  // In the event that no Goals were resolved through recommendations,
  // fallback to recommending all the goals
  if (!activeGoals?.length) {
    activeGoals = outcome?.goals;
  }

  const { toggleGoal, includedGoals } = useIncludedGoals(
    activeGoals.map(({ id }) => id)
  );

  const contextToProvide = {
    outcome: outcome,
    activeGoals: activeGoals || [],
    allGoals: resolve(outcome, 'goals', []),
    toggleGoal,
  };

  const handleReject = async () => {
    await rejectRecommendationAction(
      outcomeToEdit,
      configuration,
      reloadOutcomes
    );
  };

  const addOutcome = async () => {
    try {
      const { data } = await addOutcomeMutation({
        uuid: user.uuid,
        outcomeId: outcome.id,
        includedGoalIds: includedGoals,
        isRecommendation,
      });
      const { success, errors } = addOutcomeMutationParseQuery(data);
      const notificationType = success ? 'success' : 'error';
      const message = success
        ? `The ${resolve(
            configuration,
            'environment/journey/goals_name',
            'outcome'
          )} has successfully been added.`
        : `There was an error adding your ${resolve(
            configuration,
            'environment/journey/goals_name',
            'outcome'
          )}`;
      NotificationRender(notificationType, message);
      reloadOutcomes();
      if (resolve(errors, 'length', 0) > 0) console.error(errors);
    } catch (e) {
      console.error(e);
      NotificationRender(
        'error',
        `There was an error adding your ${resolve(
          configuration,
          'environment/journey/goals_name',
          'outcome'
        )}`
      );
    }
  };

  /**
   * Submits an outcome for association to the user in context by default.
   *
   * If the prop `handleAddOutcome` is present and is a function
   * then the outcome is being added/recommended from the professional center or
   * otherwise being added/recommended by someone other than the user in context.
   *
   */
  const handleSubmitOutcome = async () => {
    if (typeof handleAddOutcome === 'function') {
      await handleAddOutcome({
        user_uuids: [user.uuid],
        outcome_id: outcome.id,
        included_goal_ids: includedGoals,
        outcome_name: outcome.name,
      });
    } else {
      await addOutcome();
      await reloadOutcomes();
    }
    if (typeof reloadParentGraph === 'function') await reloadParentGraph();
  };

  return (
    <OutcomeGoalContext.Provider value={contextToProvide}>
      <p className="recommendationDescription">
        {headerDescription
          ? headerDescription
          : `Choose which ${resolve(
              configuration,
              'environment/journey/goals_name',
              'goal'
            )}s you would like to set for yourself. New ${resolve(
              configuration,
              'environment/journey/goals_name',
              'goal'
            )}s will be added to the top of your list.`}
      </p>
      <OutcomeCard outcome={outcome} canToggleGoals={true} />
      <StyledOutcomesModal.Footer canSubmit={true}>
        <button
          type="button"
          className="cancelButton"
          onClick={(e) => handleCancel({ e, ...props })}
        >
          Back
        </button>
        {isRecommendation && (
          <button type="button" className="rejectButton" onClick={handleReject}>
            Reject Recommendation
          </button>
        )}
        <ButtonDisabler
          ButtonComponent={'button'}
          className="confirmButton"
          disabled={false}
          canSubmit={true}
          handleClick={handleSubmitOutcome}
          buttonText={
            actionButtonText
              ? actionButtonText
              : `Add ${capitalize(
                  resolve(
                    configuration,
                    'environment/journey/goals_name',
                    'outcome'
                  )
                )}`
          }
        />
      </StyledOutcomesModal.Footer>
    </OutcomeGoalContext.Provider>
  );
};

OutcomeGoalsCard.propTypes = {
  outcome: object,
  configuration: object,
  reloadParentGraph: func,
  handle_graph_loading: func,
  handleAddOutcome: func,
  actionButtonText: string,
  headerDescription: string,
  user: object,
  graph_loading: boolean,
  graph_data: object,
  recommend_to_user: object,
  reloadOutcomes: func,
};

OutcomeGoalsCard.query = async (token, { outcome_id }) => {
  return await outcomeQuery(outcome_id);
};

OutcomeGoalsCard.hoc_list = [
  'withUser',
  withRouter,
  'withConfig',
  'withReloadParent',
  'withData',
  'withUserOutcomes',
];

export default hasHoc(OutcomeGoalsCard);
